import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Container, Typography, Box, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Alert, LinearProgress,
  IconButton, Chip, Stack, useTheme, useMediaQuery, Card, CardContent, Divider
} from '@mui/material';
import { CloudUpload, Download, Close, CheckCircle, Error, GroupAdd } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';

const ImportUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(selectedFile.type)) {
        snackbar('فقط فایل‌های اکسل (.xlsx یا .xls) مجاز هستند');
        return;
      }

      // Check file size (5MB max)
      if (selectedFile.size > 5 * 1024 * 1024) {
        snackbar('حجم فایل نباید بیشتر از 5 مگابایت باشد');
        return;
      }

      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      snackbar('لطفاً ابتدا یک فایل انتخاب کنید');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const response = await api.post('/users/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      if (response.data.success > 0) {
        successMessage(`${response.data.success} کاربر با موفقیت وارد شد`);
      }
      if (response.data.failed > 0) {
        snackbar(`${response.data.failed} کاربر وارد نشد`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      snackbar(error.response?.data?.message || 'خطا در آپلود فایل');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
  };

  const downloadTemplate = () => {
    // داده نمونه برای xlsx
    const sampleData = [
      {
        phone: '09123456789',
        firstName: 'علی',
        lastName: 'احمدی',
        nationalityCode: '0123456789',
        medicalSystemNumber: '12345',
        sex: '2',
        'userRole (اختیاری)': ''
      },
      {
        phone: '09987654321',
        firstName: 'فاطمه',
        lastName: 'محمدی',
        nationalityCode: '9876543210',
        medicalSystemNumber: '67890',
        sex: '1',
        'userRole (اختیاری)': ''
      }
    ];
    const ws = XLSX.utils.json_to_sheet(sampleData, {header: [
      'phone', 'firstName', 'lastName', 'nationalityCode', 'medicalSystemNumber', 'sex', 'userRole (اختیاری)']});
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'} 
        justifyContent="space-between" 
        alignItems={isMobile ? 'flex-start' : 'center'} 
        gap={2}
        mb={4}
      >
        <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary" gutterBottom>
              وارد کردن کاربران از فایل اکسل
            </Typography>
            <Typography variant="body2" color="text.secondary">
              فایل اکسل خود را آپلود کنید تا کاربران به صورت دسته‌جمعی وارد شوند
            </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={downloadTemplate}
          fullWidth={isMobile}
          sx={{ borderRadius: 3, px: 3 }}
        >
          دانلود فایل نمونه
        </Button>
      </Box>

      {/* Instructions */}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main">
          راهنمای ستون‌های فایل اکسل
        </Typography>
        <Typography variant="body2" component="div" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          <ul>
            <li><strong>phone</strong> (الزامی): شماره تلفن کاربر</li>
            <li><strong>firstName</strong>: نام</li>
            <li><strong>lastName</strong>: نام خانوادگی</li>
            <li><strong>nationalityCode</strong>: کد ملی</li>
            <li><strong>medicalSystemNumber</strong>: شماره نظام پزشکی</li>
            <li><strong>sex</strong>: 0 (نامشخص)، 1 (زن)، 2 (مرد)</li>
            <li><strong>userRole</strong> (اختیاری): user یا admin</li>
          </ul>
        </Typography>
      </Paper>

      {/* File Upload */}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
        <Stack spacing={2}>
          {!file ? (
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'primary.main',
                borderRadius: 4,
                p: isMobile ? 4 : 6,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: 'primary.lighter',
                transition: 'all 0.3s ease',
                '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.dark' },
              }}
              component="label"
            >
              <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                انتخاب فایل اکسل
              </Typography>
              <Typography variant="body2" color="text.secondary">
                فایل را اینجا رها کنید یا کلیک کنید
              </Typography>
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
            </Box>
          ) : (
            <Box>
              <Alert
                severity="info"
                icon={<GroupAdd />}
                sx={{ borderRadius: 3, mb: 2 }}
                action={
                  <IconButton size="small" onClick={handleReset}>
                    <Close />
                  </IconButton>
                }
              >
                فایل انتخاب شده: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
              </Alert>
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CloudUpload />}
                  onClick={handleUpload}
                  disabled={loading}
                  fullWidth
                  sx={{ borderRadius: 2, py: 1.5 }}
                >
                  {loading ? 'در حال پردازش...' : 'شروع عملیات وارد کردن'}
                </Button>
                <Button 
                    variant="outlined" 
                    onClick={handleReset} 
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{ borderRadius: 2 }}
                >
                  انصراف
                </Button>
              </Box>
            </Box>
          )}

          {loading && (
             <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" align="center" display="block" mb={1}>
                    سیستم در حال تحلیل فایل و ایجاد رکوردهای جدید است...
                </Typography>
                <LinearProgress sx={{ borderRadius: 2, height: 8 }} />
             </Box>
          )}
        </Stack>
      </Paper>

      {/* Results */}
      {result && (
        <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            نتیجه پردازش فایل
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Chip
              icon={<CheckCircle />}
              label={`موفق: ${result.success}`}
              color="success"
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
            <Chip
              icon={<Error />}
              label={`ناموفق: ${result.failed}`}
              color="error"
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
          </Stack>

          {result.errors && result.errors.length > 0 && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3, color: 'error.main' }}>
                لیست خطاهای یافت شده:
              </Typography>
              
              {isMobile ? (
                  <Stack spacing={2}>
                    {result.errors.map((error, index) => (
                        <Card key={index} variant="outlined" sx={{ borderRadius: 3, borderColor: 'error.light' }}>
                            <CardContent sx={{ p: 2 }}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle2" fontWeight="bold">ردیف: {error.row}</Typography>
                                    <Typography variant="caption" color="text.secondary">{error.phone || 'بدون شماره'}</Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2" color="error.main">
                                    {error.error}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                  </Stack>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                    <Table size="small">
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                        <TableRow>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>ردیف</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>شماره تماس</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>شرح خطا</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {result.errors.map((error, index) => (
                        <TableRow key={index} hover>
                            <TableCell align="right">{error.row}</TableCell>
                            <TableCell align="right">{error.phone || '-'}</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                                {error.error}
                            </TableCell>
                        </TableRow>
                        ))}
                    </TableBody>
                    </Table>
                </TableContainer>
              )}
            </>
          )}

          {result.success > 0 && (
            <Alert severity="success" sx={{ mt: 3, borderRadius: 3 }}>
              {result.success} کاربر با موفقیت به بانک اطلاعاتی اضافه شدند.
            </Alert>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default ImportUsers;
